import type React from 'react';
import type { LocaleCode, RecordableOperation } from '@/api/models';
import { CommandPickerView } from './CommandPickerView';
import { useCommandPicker } from './useCommandPicker';

type CommandPickerProps = {
    operations: RecordableOperation[];
    onSelect: (key: string) => void;
    locale: LocaleCode;
    /**
     * Whether the dead-end footer links to the Templates tab. False for a role
     * that may not edit them — the sentence beside it already says who can.
     */
    canEditTemplates: boolean;
};

/**
 * Which operation is being recorded.
 *
 * Fed rather than self-served: the page owns the read, because the list is a
 * function of the date above it and both the picker and the date band have to be
 * describing the same fetch. What this container owns is the picker's own state
 * — the query, the sort, and where the keyboard is.
 *
 * It renders **no loading and no empty state**. The page decides both: an empty
 * list is a fact about the date or the role, and saying which needs counts this
 * component is not given.
 *
 * It has **no selected row** either. Since BOS-149 picking replaces the picker
 * with the collapsed header, so this only ever renders while nothing is chosen —
 * a highlighted "current" row would be describing a state that unmounts it.
 */
export const CommandPicker: React.FC<CommandPickerProps> = ({
    operations,
    onSelect,
    locale,
    canEditTemplates,
}) => {
    const picker = useCommandPicker({ operations, locale, onSelect });

    return (
        <CommandPickerView
            canEditTemplates={canEditTemplates}
            groups={picker.groups}
            highlight={picker.highlight}
            locale={locale}
            onHighlight={picker.onHighlight}
            onQueryChange={picker.onQueryChange}
            onSearchKeyDown={picker.onSearchKeyDown}
            onSelect={onSelect}
            onSortChange={picker.onSortChange}
            query={picker.query}
            shortcuts={picker.shortcuts}
            sort={picker.sort}
        />
    );
};
